(() => {
  'use strict';

  const ENGINE_VERSION = 2;
  const GARAGE_KEY = 'kraven-garage:v2';
  const PREVIOUS_V2_GARAGE_KEY = 'kraven-garage-v2';
  const LEGACY_GARAGE_KEY = 'kraven-garage';
  const CATALOG_SELECTOR = '[data-kraven-vehicle-catalog]';
  const PRODUCT_FITMENT_SELECTOR = '[data-kraven-product-fitment]';
  const PRODUCT_LIMIT = 8;
  const UNSURE_TRIM = 'unsure';
  const UNSURE_TRIM_LABEL = 'Unsure — confirm later';

  const copy = {
    select: 'Select',
    loading: 'Loading compatible vehicles…',
    loadError: 'Fitment inventory could not be loaded. You can still browse the vehicle collection.',
    incomplete: 'Select every vehicle detail to check compatibility.',
    match: 'Confirmed for your selected vehicle.',
    review: 'The vehicle range matches, but a visual or factory-part check is still required.',
    reviewSaved: 'Vehicle range matched. Your visual fitment confirmation is saved with the item.',
    unsureMatch: 'Your vehicle range matches. The unsure detail is saved with your order for confirmation.',
    incompatible: 'This product is not a confirmed match for your selected vehicle.',
  };

  const normalize = (value) => String(value ?? '').trim().toLowerCase();
  const unique = (values) => [...new Set(values.filter(Boolean))];
  const asArray = (value) => (Array.isArray(value) ? value : value == null ? [] : [value]);
  const numberArray = (value) => asArray(value).map(Number).filter(Number.isFinite);
  const equal = (left, right) => normalize(left) === normalize(right);
  const isUnsureTrim = (value) => normalize(value) === UNSURE_TRIM;

  function isUnsureAnswer(value) {
    const selected = normalize(value);
    return (
      selected === 'unsure' ||
      selected === 'not sure' ||
      selected === "don't know" ||
      selected === 'do not know' ||
      selected.includes('not sure') ||
      selected.includes('unsure')
    );
  }

  function questionDisplayLabel(question) {
    const rawKey = String(question?.key || question?.id || '');
    const key = normalize(rawKey).replace(/[^a-z0-9]/g, '');
    const labels = {
      transmission: 'Factory automatic shift knob',
      factoryknobmatch: 'Factory automatic shift knob',
      factorypaddles: 'Factory paddle shifters',
      factorypaddleshifters: 'Factory paddle shifters',
      frontsmartentryopenings: 'Smart-entry buttons on front handles',
      frontunlockbuttons: 'Smart-entry buttons on front handles',
      driverkeyhole: 'Driver-side keyhole',
      factorymirror: 'Camera under the mirror',
      hudconfiguration: 'Head-up display (HUD)',
      factorylights: 'Factory tail-light type',
      rearaxle: 'Rear axle',
      bodygeneration: '2019 body style',
      wheelshape: 'Factory steering-wheel trim',
    };
    if (labels[key]) return labels[key];

    const sourceLabel = String(question?.label || 'Vehicle equipment');
    if (!/(?:photo|picture|image)/i.test(sourceLabel)) return sourceLabel;

    // Future imports sometimes describe fitment as “match the pictures.” Keep
    // the stored key/value stable for rule evaluation, but turn that wording
    // into a short customer-facing factory-feature label.
    const inferredLabel = rawKey
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\b(?:match|matches|matching|photo|photos|picture|pictures|image|images|check|confirmation)\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!inferredLabel) return 'Factory part configuration';
    return inferredLabel.charAt(0).toUpperCase() + inferredLabel.slice(1);
  }

  function questionOptionLabel(question, option) {
    const value = String(option ?? '');
    const selected = normalize(value);
    const key = normalize(question?.key || question?.id).replace(/[^a-z0-9]/g, '');
    if (isUnsureAnswer(value)) return 'Not sure';

    if (key === 'transmission' || key === 'factoryknobmatch') {
      if (/factory automatic/.test(selected) || /^yes\b/.test(selected)) return 'Yes — factory automatic shift knob';
      if (/manual|aftermarket|converted|^no\b/.test(selected)) return 'No — manual, aftermarket or converted shifter';
    }
    if (key === 'factorypaddles' || key === 'factorypaddleshifters') {
      if (/present|^yes\b/.test(selected)) return 'Yes — factory paddle shifters';
      if (/not present|without|^no\b/.test(selected)) return 'No factory paddle shifters';
    }
    if (key === 'frontsmartentryopenings' || key === 'frontunlockbuttons') {
      if (/both/.test(selected)) return 'Buttons on both front handles';
      if (/one|neither|different|missing|^no\b/.test(selected)) return 'Button on one or neither front handle';
    }
    if (key === 'driverkeyhole') {
      if (/^yes\b|^matches?\b/.test(selected)) return 'Yes — driver-side keyhole';
      if (/^no\b|does not match|different|missing/.test(selected)) return 'No driver-side keyhole';
    }
    if (key === 'factorymirror') {
      if (/no 360|without.*360/.test(selected)) return 'No 360-degree camera';
      if (/has.*360|with.*360/.test(selected)) return 'Has a 360-degree camera';
    }
    if (/does not match|different/.test(selected)) return 'No — different factory part';
    if (/matches?.*(?:photo|picture)|exact match/.test(selected)) return 'Yes — matching factory part';
    return value;
  }

  function trimOptions(trims) {
    if (!trims?.length) return [];
    return [
      { value: UNSURE_TRIM, label: UNSURE_TRIM_LABEL },
      ...trims.map((value) => ({ value, label: value })),
    ];
  }

  function parseJson(value) {
    if (value && typeof value === 'object') return value;
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  function embeddedJson(selector, scope = document) {
    const local = scope?.querySelector?.(selector);
    const node = local || document.querySelector(selector);
    if (!node) return null;

    const attributeValue =
      node.getAttribute('data-json') ||
      node.getAttribute(selector.slice(1, -1)) ||
      node.getAttribute('value');

    return parseJson(node.textContent) || parseJson(attributeValue);
  }

  function normalizeCatalog(input) {
    const parsed = parseJson(input) || input;
    const catalog = parsed?.value && typeof parsed.value === 'object' ? parsed.value : parsed;
    const vehicles = asArray(catalog?.vehicles)
      .filter((vehicle) => vehicle && vehicle.key && vehicle.make && vehicle.model)
      .map((vehicle) => ({
        ...vehicle,
        years: numberArray(vehicle.years),
      }));

    return {
      version: Number(catalog?.version) || ENGINE_VERSION,
      vehicles,
    };
  }

  function inferenceText(product) {
    return normalize([
      product?.title,
      product?.handle,
      product?.vendor,
      product?.type,
      ...asArray(product?.tags),
    ].join(' '));
  }

  function positiveInferenceText(product) {
    const positiveTags = asArray(product?.tags).filter(
      (tag) => !/not compatible|not confirmed|does not fit|doesn't fit|excluded?|unsupported/i.test(String(tag))
    );
    return normalize([
      product?.title,
      product?.handle,
      product?.vendor,
      product?.type,
      ...positiveTags,
    ].join(' '));
  }

  function inferenceYears(product) {
    // Negative catalog tags such as “2025 Camry Not Compatible” must never
    // expand a product's fallback year range.
    const text = positiveInferenceText(product);
    const years = new Set();

    for (const match of text.matchAll(/\b20(?:1[8-9]|2[0-6])\b/g)) years.add(Number(match[0]));
    for (const match of text.matchAll(/\b(20\d{2})\s*(?:-|–|—|to)\s*((?:20)?\d{2})\b/g)) {
      const start = Number(match[1]);
      const rawEnd = String(match[2]);
      const end = rawEnd.length === 2 ? Number(`20${rawEnd}`) : Number(rawEnd);
      if (!Number.isFinite(start) || !Number.isFinite(end) || end < start || end - start > 12) continue;
      for (let year = start; year <= end; year += 1) years.add(year);
    }

    return [...years].sort((left, right) => left - right);
  }

  function inferredDepartment(product) {
    const text = inferenceText(product);
    if (/krvn department interior|\binterior\b|shift knob|steering wheel/.test(text)) return 'Interior';
    if (
      /krvn department lighting|headlights?|tail\s*lights?|fog\s*lights?|\bdrl\b|reflector\s*lights?|lighting/.test(
        text
      )
    ) {
      return 'Lighting';
    }
    return 'Exterior';
  }

  function inferredCategoryCollection(vehicleKey, product, department) {
    const text = inferenceText(product);
    if (vehicleKey === 'toyota-camry-xv70') {
      if (/fog\s*lights?|\bdrl\b/.test(text)) return '/collections/camry-8th-gen-fog-lights-drls';
      if (/tail\s*lights?/.test(text)) return '/collections/camry-8th-gen-tail-lights';
      if (/reflector\s*lights?/.test(text)) return '/collections/camry-8th-gen-bumper-reflector-lights';
      if (department === 'Lighting') return '/collections/camry-8th-gen-lighting';
      return '/collections/camry-8th-gen-2018-2024';
    }

    if (/front (?:bumper )?(?:lip|splitter)/.test(text)) return '/collections/camry-front-lips-splitters';
    if (/side skirts?/.test(text)) return '/collections/camry-side-skirts';
    if (/trunk spoilers?/.test(text)) return '/collections/camry-trunk-spoilers';
    if (/roof spoilers?/.test(text)) return '/collections/camry-roof-spoilers';
    if (/window visors?|rain guards?/.test(text)) return '/collections/camry-window-accessories';
    if (/shift knobs?/.test(text)) return '/collections/camry-shift-knobs';
    if (/reflector\s*lights?/.test(text)) return '/collections/camry-bumper-reflector-lights';
    if (department === 'Lighting') return '/collections/camry-lighting-upgrades';
    if (department === 'Interior') return '/collections/2025-2026-toyota-camry-interior-accessories';
    return '/collections/2025-2026-toyota-camry-exterior-accessories';
  }

  function inferProductFitment(product) {
    const text = inferenceText(product);
    if (!/\bcamry\b/.test(text)) return null;

    const detectedYears = inferenceYears(product);
    const hasXv70Marker = /8th gen camry|xv70 camry|vehicle camry 2018/.test(text);
    const hasXv80Marker = /9th gen camry|xv80 camry|vehicle camry 2025/.test(text);
    const xv70Years = detectedYears.filter((year) => year >= 2018 && year <= 2024);
    const xv80Years = detectedYears.filter((year) => year >= 2025 && year <= 2026);
    const department = inferredDepartment(product);
    const applications = [];

    if (hasXv70Marker || xv70Years.length) {
      applications.push({
        id: `inferred-${product?.handle || 'camry'}-xv70`,
        vehicleKey: 'toyota-camry-xv70',
        years: xv70Years.length ? xv70Years : [2018, 2019, 2020, 2021, 2022, 2023, 2024],
        trims: ['*'],
        conditions: {},
        verification: 'review',
        note: 'Generation and model years are matched from the product fitment tags. Review the product page for trim and factory-configuration requirements.',
      });
    }

    if (hasXv80Marker || xv80Years.length) {
      applications.push({
        id: `inferred-${product?.handle || 'camry'}-xv80`,
        vehicleKey: 'toyota-camry-xv80',
        years: xv80Years.length ? xv80Years : [2025, 2026],
        trims: ['*'],
        conditions: {},
        verification: 'review',
        note: 'Generation and model years are matched from the product fitment tags. Review the product page for trim and factory-configuration requirements.',
      });
    }

    if (!applications.length) return null;
    const primaryKey = applications[0].vehicleKey;
    const vehicleCollection =
      primaryKey === 'toyota-camry-xv70'
        ? '/collections/camry-8th-gen-2018-2024'
        : '/collections/camry-9th-gen-2025-2026';

    return {
      version: 1,
      policy: 'visual_review',
      inferred: true,
      department,
      vehicleCollection,
      categoryCollection: inferredCategoryCollection(primaryKey, product, department),
      questions: [],
      alternatives: [],
      applications,
    };
  }

  function normalizeFitment(input) {
    const parsed = parseJson(input) || input;
    const fitment = parsed?.value && typeof parsed.value === 'object' ? parsed.value : parsed;
    if (!fitment || typeof fitment !== 'object') return null;

    const questions = asArray(fitment.questions)
      .map((question, index) => {
        if (!question || typeof question !== 'object' || !question.label) return null;
        const sourceKey = String(question.key || question.id || question.name || '').trim();
        if (!sourceKey) return null;
        const key = sourceKey;
        const normalizedKey = normalize(key).replace(/[^a-z0-9]/g, '');
        const normalizedLabel = normalize(question.label);

        // Make/model/year/trim already have dedicated cascading fields. Do
        // not render a second trim selector when imported metadata includes one.
        if (normalizedKey === 'trim' || normalizedKey === 'camrytrim' || normalizedLabel === 'camry trim') {
          return null;
        }

        const options = asArray(question.options).filter((option) => option != null && String(option).trim());
        if (!options.length) return null;
        return {
          ...question,
          key,
          sourceKey,
          options,
          _index: index,
        };
      })
      .filter(Boolean);
    const conditionAliases = {
      factoryPaddles: ['factoryPaddleShifters', 'factoryPaddles'],
      factoryLights: ['factoryLights', 'factoryLightType'],
      rearAxle: ['rearAxle'],
      bodyGeneration: ['bodyGeneration'],
      wheelShape: ['wheelShape'],
      transmission: ['transmission'],
    };

    const applications = asArray(fitment.applications || fitment.rules)
      .filter((application) => application && application.vehicleKey)
      .map((application, index) => {
        const rawConditions =
          application.conditions && typeof application.conditions === 'object' ? application.conditions : {};
        const conditions = {};

        questions.forEach((question) => {
          const candidates = unique([
            question.key,
            question.sourceKey,
            ...asArray(conditionAliases[question.key]),
          ]);
          const conditionKey = candidates.find((candidate) => Object.hasOwn(rawConditions, candidate));
          if (!conditionKey) return;
          conditions[question.key] = rawConditions[conditionKey];
        });

        const rawVerification = normalize(application.verification);
        const hasInteractiveConditions = Object.keys(conditions).length > 0;
        const verification =
          rawVerification === 'conditional'
            ? hasInteractiveConditions
              ? 'confirmed'
              : 'review'
            : application.verification;

        return {
          ...application,
          id: application.id || application.applicationId || `application-${index + 1}`,
          years: numberArray(application.years),
          trims: asArray(application.trims),
          conditions,
          // Descriptive conditions are useful reference metadata, but they are
          // not customer inputs. Respect an explicit confirmed fit; conditional
          // records only become confirmed after their mapped question is answered.
          verification,
        };
      });

    if (!applications.length && fitment.inference) {
      const inferred = inferProductFitment(fitment.inference);
      if (inferred) return normalizeFitment(inferred);
    }

    return {
      ...fitment,
      version: Number(fitment.version) || 1,
      applications,
      questions,
      alternatives: asArray(fitment.alternatives).filter(Boolean),
    };
  }

  function profileYears(profile) {
    if (profile?.years?.length) return profile.years;
    const first = Number(profile?.yearStart);
    const last = Number(profile?.yearEnd);
    if (!Number.isFinite(first) || !Number.isFinite(last) || last < first) return [];
    return Array.from({ length: last - first + 1 }, (_, index) => first + index);
  }

  function profileTrims(profile, year) {
    const yearKey = String(year || '');
    const byYear = profile?.trimsByYear?.[yearKey];
    return unique(asArray(byYear?.length ? byYear : profile?.trims));
  }

  function applicationMatchesTrim(application, trim) {
    const trims = asArray(application?.trims);
    if (!trims.length || trims.some((value) => value === '*')) return true;
    if (!trim) return false;
    if (isUnsureTrim(trim)) return true;
    if (
      trims.length === 1 &&
      /equipped|applicable|with factory|factory .* required|trims? only/.test(normalize(trims[0]))
    ) {
      return true;
    }
    const selected = normalize(trim);
    return trims.some((value) => {
      const candidate = normalize(value);
      if (candidate === selected) return true;
      if (candidate === 'nightshade' && selected.includes('nightshade')) return true;
      if (candidate === 'hybrid' && selected.includes('hybrid')) return true;
      if (selected === 'nightshade' && candidate.includes('nightshade')) return true;
      if (selected === 'hybrid' && candidate.includes('hybrid')) return true;
      return false;
    });
  }

  function applicationMatchesConditions(application, answers, allowUnanswered = true) {
    return Object.entries(application?.conditions || {}).every(([key, allowed]) => {
      const value = answers?.[key];
      if (!value) return allowUnanswered;
      return asArray(allowed).some((candidate) => equal(candidate, value));
    });
  }

  function matchingApplications(fitment, selection, options = {}) {
    if (!fitment || !selection?.vehicleKey || !Number.isFinite(Number(selection.year))) return [];
    const year = Number(selection.year);
    return fitment.applications.filter((application) => {
      if (application.vehicleKey !== selection.vehicleKey) return false;
      if (!application.years.includes(year)) return false;
      if (!applicationMatchesTrim(application, selection.trim)) return false;
      return applicationMatchesConditions(application, selection.answers, options.allowUnanswered !== false);
    });
  }

  function productPotentiallyMatches(product, selection, useAnswers = false) {
    const fitment = normalizeFitment(product?.fitment);
    if (!fitment) return false;
    return matchingApplications(fitment, selection, { allowUnanswered: !useAnswers }).length > 0;
  }

  function departmentValue(value) {
    const normalized = normalize(value);
    if (normalized === 'exterior') return 'exterior';
    if (normalized === 'lighting') return 'lighting';
    if (normalized === 'interior') return 'interior';
    return 'all';
  }

  function departmentLabel(value) {
    const normalized = departmentValue(value);
    if (normalized === 'exterior') return 'Exterior';
    if (normalized === 'lighting') return 'Lighting';
    if (normalized === 'interior') return 'Interior';
    return 'All upgrades';
  }

  function selectField(root, name) {
    const selectors = [
      `[data-fitment-${name}]`,
      `[data-verify-${name}]`,
      `[data-fitment-field][name="${name}"]`,
      `select[name="${name}"]`,
    ];
    return root.querySelector(selectors.join(','));
  }

  function fieldShell(field) {
    return field?.closest('[data-fitment-field-shell], .kraven-fitment__field, label') || null;
  }

  function showField(field, visible) {
    if (!field) return;
    const shell = fieldShell(field);
    if (shell) shell.hidden = !visible;
    field.disabled = !visible;
    field.required = visible;
    field.setAttribute('aria-hidden', String(!visible));
  }

  function setOptions(select, options, settings = {}) {
    if (!select) return '';
    const placeholder = settings.placeholder || copy.select;
    const normalizedOptions = options.map((option) =>
      typeof option === 'string' ? { value: option, label: option } : option
    );
    const requested = String(settings.value ?? select.value ?? '');
    const desired =
      !requested && settings.autoSelectSingle && normalizedOptions.length === 1
        ? String(normalizedOptions[0].value)
        : requested;
    const signature = JSON.stringify([placeholder, normalizedOptions]);

    if (select.dataset.kravenOptions !== signature) {
      const fragment = document.createDocumentFragment();
      const empty = document.createElement('option');
      empty.value = '';
      empty.textContent = placeholder;
      fragment.append(empty);

      normalizedOptions.forEach((option) => {
        const node = document.createElement('option');
        node.value = String(option.value);
        node.textContent = String(option.label);
        fragment.append(node);
      });

      select.replaceChildren(fragment);
      select.dataset.kravenOptions = signature;
    }

    const exists = [...select.options].some((option) => option.value === desired);
    select.value = exists ? desired : '';
    return select.value;
  }

  function removeVehicleType(root) {
    root.querySelectorAll('[name="vehicle_type"], [data-fitment-vehicle-type]').forEach((field) => {
      const shell = fieldShell(field);
      if (shell) shell.remove();
      else field.remove();
    });
  }

  function profileCandidates(profiles, values) {
    const year = Number(values.year);
    return profiles.filter((profile) => {
      if (!equal(profile.make, values.make) || !equal(profile.model, values.model)) return false;
      return !Number.isFinite(year) || profileYears(profile).includes(year);
    });
  }

  function resolveProfile(profiles, values, preferredKeys = []) {
    const candidates = profileCandidates(profiles, values);
    if (!candidates.length) return null;

    const remembered = candidates.find((profile) => profile.key === values.vehicleKey);
    if (remembered) return remembered;

    const preferred = candidates.filter((profile) => preferredKeys.includes(profile.key));
    const source = preferred.length ? preferred : candidates;
    return [...source].sort((left, right) => {
      const rangeDifference = profileYears(left).length - profileYears(right).length;
      return rangeDifference || String(left.key).localeCompare(String(right.key));
    })[0];
  }

  function activeVehicleProfiles(catalog, products) {
    if (!products?.length) return catalog.vehicles;
    const keys = new Set();
    products.forEach((product) => {
      normalizeFitment(product.fitment)?.applications.forEach((application) => keys.add(application.vehicleKey));
    });
    return catalog.vehicles.filter((profile) => keys.has(profile.key));
  }

  function defaultFeedUrl(source) {
    const configured =
      source?.dataset?.feedUrl ||
      source?.dataset?.fitmentFeedUrl ||
      document.querySelector('[data-kraven-fitment-feed-url]')?.dataset?.kravenFitmentFeedUrl;
    if (configured) return new URL(configured, window.location.origin).toString();
    const root = window.Shopify?.routes?.root || '/';
    return new URL(`${root}collections/all?view=fitment`, window.location.origin).toString();
  }

  let feedPromise;

  // Let input and scrolling run between catalog batches, including on older browsers.
  const yieldToBrowser = () => window.scheduler?.yield
    ? window.scheduler.yield()
    : new Promise((resolve) => window.setTimeout(resolve, 0));

  async function loadFitmentFeed(source) {
    if (feedPromise) return feedPromise;

    feedPromise = (async () => {
      const products = [];
      const seenProducts = new Set();
      const seenPages = new Set();
      let catalog = null;
      let currency = window.Shopify?.currency?.active || 'USD';
      let nextUrl = defaultFeedUrl(source);

      for (let page = 0; nextUrl && page < 20; page += 1) {
        const url = new URL(nextUrl, window.location.origin);
        if (!url.searchParams.has('view')) url.searchParams.set('view', 'fitment');
        if (seenPages.has(url.toString())) break;
        seenPages.add(url.toString());

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(`Fitment feed returned ${response.status}`);

        const payload = JSON.parse(await response.text());
        if (!payload || !Array.isArray(payload.products)) throw new Error('Fitment feed is malformed');
        catalog ||= payload.catalog || null;
        currency = payload.currency || currency;

        let batchStarted = performance.now();
        for (const product of payload.products) {
          if (!product?.handle || seenProducts.has(product.handle)) continue;
          seenProducts.add(product.handle);
          const fitmentSource = product.fitment || { inference: product };
          products.push({ ...product, fitment: normalizeFitment(fitmentSource) });
          if (performance.now() - batchStarted >= 8) {
            await yieldToBrowser();
            batchStarted = performance.now();
          }
        }

        nextUrl = payload.pagination?.next || null;
      }

      return { version: ENGINE_VERSION, currency, catalog: normalizeCatalog(catalog), products };
    })().catch((error) => {
      feedPromise = null;
      throw error;
    });

    return feedPromise;
  }

  class GarageStore {
    static read(storageKey = GARAGE_KEY) {
      try {
        const v2Keys = unique([storageKey, GARAGE_KEY, PREVIOUS_V2_GARAGE_KEY]);
        for (const key of v2Keys) {
          const current = parseJson(localStorage.getItem(key));
          if (current?.version === ENGINE_VERSION) return current;
        }

        const legacy = parseJson(localStorage.getItem(LEGACY_GARAGE_KEY));
        if (!legacy) return { version: ENGINE_VERSION, productAnswers: {} };
        return {
          version: ENGINE_VERSION,
          vehicleKey: legacy.vehicleKey || '',
          make: legacy.make || '',
          model: legacy.model || '',
          year: Number(legacy.year) || '',
          trim: legacy.trim || legacy.configuration || '',
          category: legacy.category || 'all',
          productAnswers: {},
          updatedAt: Date.now(),
        };
      } catch {
        return { version: ENGINE_VERSION, productAnswers: {} };
      }
    }

    static write(update, storageKey = GARAGE_KEY) {
      const current = GarageStore.read(storageKey);
      const next = {
        ...current,
        ...update,
        version: ENGINE_VERSION,
        productAnswers: update.productAnswers || current.productAnswers || {},
        updatedAt: Date.now(),
      };
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Storage can be unavailable in private browsing. Fitment still works for the current page.
      }
      return next;
    }
  }

  function withGarageParameters(destination, selection, category = 'all') {
    const url = new URL(destination || '/collections/all', window.location.origin);
    if (selection.vehicleKey) url.searchParams.set('kraven_vehicle', selection.vehicleKey);
    if (selection.year) url.searchParams.set('kraven_year', String(selection.year));
    if (selection.trim) url.searchParams.set('kraven_trim', selection.trim);
    if (category && category !== 'all') url.searchParams.set('kraven_category', category);
    return `${url.pathname}${url.search}${url.hash}`;
  }

  function productHandleFromLocation() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function formatMoney(cents, currency) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: amount % 100 === 0 ? 0 : 2,
      }).format(amount / 100);
    } catch {
      return `$${(amount / 100).toFixed(2)}`;
    }
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function productCard(product, currency, badgeText = 'For your vehicle') {
    const card = element('article', 'kraven-fitment-card');
    const link = element('a', 'kraven-fitment-card__link');
    link.href = product.url;
    link.setAttribute('aria-label', product.title);

    const media = element('span', 'kraven-fitment-card__media');
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);
    }

    const content = element('span', 'kraven-fitment-card__content');
    const badge = element('span', 'kraven-fitment-card__badge', product.available ? badgeText : 'Currently unavailable');
    const title = element('strong', 'kraven-fitment-card__title', product.title);
    const details = element('span', 'kraven-fitment-card__details');
    const type = element('span', 'kraven-fitment-card__type', product.type || departmentLabel(product.fitment?.department));
    const price = element('span', 'kraven-fitment-card__price', formatMoney(product.price, currency));
    details.append(type, price);

    const compareAt = Number(product.compareAt);
    if (compareAt > Number(product.price)) {
      const compare = element('s', 'kraven-fitment-card__compare', formatMoney(compareAt, currency));
      details.append(compare);
    }

    content.append(badge, title, details);
    link.append(media, content);
    card.append(link);
    return card;
  }

  class KravenFitmentFinder extends window.HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      removeVehicleType(this);
      this.form = this.querySelector('[data-fitment-form]') || this.querySelector('form');
      this.status = this.querySelector('[data-fitment-status]');
      this.resultsRoot = this.querySelector('[data-fitment-results]');
      this.results = this.querySelector('[data-fitment-cards], [data-fitment-products]');
      this.categoryCounts = this.querySelector('[data-fitment-category-counts]');
      this.resultCount = this.querySelector('[data-fitment-count], [data-fitment-result-count]');
      this.viewAll = this.querySelector('[data-fitment-view-all]');
      this.submitButton = this.querySelector('[data-fitment-submit], button[type="submit"]');
      this.fields = {
        make: selectField(this, 'make'),
        model: selectField(this, 'model'),
        year: selectField(this, 'year'),
        trim: selectField(this, 'trim'),
        category: selectField(this, 'category'),
      };
      this.embeddedCatalog = normalizeCatalog(embeddedJson(CATALOG_SELECTOR, this));
      this.storageKey = this.dataset.storageKey || GARAGE_KEY;
      this.ready = false;

      this.form?.addEventListener('submit', (event) => this.submit(event), { signal });
      this.form?.addEventListener('change', (event) => this.change(event), { signal });
      this.addEventListener('focusin', () => this.load(), { once: true, signal });
      this.addEventListener('pointerenter', () => this.load(), { once: true, signal });

      this.setLoading(true);
      this.startLazyLoad();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
    }

    startLazyLoad() {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '700px 0px' }
        );
        this.observer.observe(this);
        return;
      }

      const schedule = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 1));
      schedule(() => this.load(), { timeout: 1800 });
    }

    setLoading(isLoading) {
      this.setAttribute('aria-busy', String(isLoading));
      this.dataset.fitmentState = isLoading ? 'loading' : this.dataset.fitmentState || 'idle';
      if (isLoading) {
        if (this.submitButton) this.submitButton.disabled = true;
        Object.values(this.fields).forEach((field) => {
          if (field) field.disabled = true;
        });
        if (this.fields.make) setOptions(this.fields.make, [], { placeholder: copy.loading });
      } else {
        this.refreshDependencies();
      }
    }

    async load() {
      if (this.loadPromise) return this.loadPromise;
      this.loadPromise = (async () => {
        try {
          this.feed = await loadFitmentFeed(this);
          const catalog = this.feed.catalog.vehicles.length ? this.feed.catalog : this.embeddedCatalog;
          this.profiles = activeVehicleProfiles(catalog, this.feed.products);
          if (this.dataset.camryFocus === 'true') {
            this.profiles = this.profiles.filter(
              (profile) => equal(profile.make, 'Toyota') && equal(profile.model, 'Camry')
            );
          }
          this.products = this.feed.products;
          this.currency = this.feed.currency;
          if (!this.profiles.length) throw new Error('No active vehicle applications');
          this.ready = true;
          this.restore();
        } catch (error) {
          console.warn('[Kraven fitment] Homepage feed unavailable.', error);
          this.profiles = this.embeddedCatalog.vehicles;
          if (this.dataset.camryFocus === 'true') {
            this.profiles = this.profiles.filter(
              (profile) => equal(profile.make, 'Toyota') && equal(profile.model, 'Camry')
            );
          }
          this.products = [];
          this.currency = window.Shopify?.currency?.active || 'USD';
          this.ready = this.profiles.length > 0;
          if (this.ready) this.restore();
          this.dataset.fitmentState = 'error';
          this.message(this.dataset.messageLoadError || copy.loadError);
        } finally {
          this.setLoading(false);
          this.refreshButton();
        }
      })();
      return this.loadPromise;
    }

    message(value) {
      if (this.status) this.status.textContent = value || '';
    }

    restore() {
      const garage = GarageStore.read(this.storageKey);
      const hasSavedVehicle = Boolean(garage.vehicleKey || garage.make || garage.model || garage.year);
      this.populate({
        make: garage.make || (!hasSavedVehicle ? this.dataset.preferredMake : ''),
        model: garage.model || (!hasSavedVehicle ? this.dataset.preferredModel : ''),
        year: garage.year,
        trim: garage.trim,
        category: departmentValue(garage.category),
        vehicleKey: garage.vehicleKey,
      });
    }

    makeOptions() {
      return unique(this.profiles.map((profile) => profile.make))
        .sort((left, right) => left.localeCompare(right))
        .map((value) => ({ value, label: value }));
    }

    modelOptions(make) {
      return unique(
        this.profiles.filter((profile) => equal(profile.make, make)).map((profile) => profile.model)
      )
        .sort((left, right) => left.localeCompare(right))
        .map((value) => ({ value, label: value }));
    }

    yearOptions(make, model) {
      return unique(
        this.profiles
          .filter((profile) => equal(profile.make, make) && equal(profile.model, model))
          .flatMap(profileYears)
      )
        .sort((left, right) => Number(right) - Number(left))
        .map((value) => ({ value: String(value), label: String(value) }));
    }

    populate(preferred = {}) {
      if (!this.ready) return;
      const make = setOptions(this.fields.make, this.makeOptions(), { value: preferred.make });
      const model = setOptions(this.fields.model, this.modelOptions(make), {
        value: preferred.model,
        autoSelectSingle: true,
      });
      const year = setOptions(this.fields.year, this.yearOptions(make, model), { value: preferred.year });
      const profile = resolveProfile(
        this.profiles,
        { make, model, year, vehicleKey: preferred.vehicleKey },
        []
      );
      const trims = profileTrims(profile, year);

      if (this.fields.trim) {
        showField(this.fields.trim, trims.length > 0);
        setOptions(
          this.fields.trim,
          trimOptions(trims),
          { value: preferred.trim }
        );
      }

      this.profile = profile;
      this.updateProducts(preferred.category);
      this.refreshDependencies();
    }

    values() {
      const trimVisible = this.fields.trim && !fieldShell(this.fields.trim)?.hidden;
      const base = {
        make: this.fields.make?.value || '',
        model: this.fields.model?.value || '',
        year: Number(this.fields.year?.value) || '',
        trim: trimVisible ? this.fields.trim.value : '',
        category: departmentValue(this.fields.category?.value),
      };
      const profile = resolveProfile(this.profiles || [], { ...base, vehicleKey: this.profile?.key });
      return { ...base, vehicleKey: profile?.key || '' };
    }

    isComplete(selection = this.values()) {
      if (!selection.make || !selection.model || !selection.year || !selection.vehicleKey) return false;
      const profile = this.profiles.find((candidate) => candidate.key === selection.vehicleKey);
      return !profileTrims(profile, selection.year).length || Boolean(selection.trim);
    }

    change(event) {
      const field = event.target.closest('select');
      if (!field || !this.form?.contains(field) || !this.ready) return;

      const current = this.values();
      if (field === this.fields.make) {
        current.make = field.value;
        current.model = '';
        current.year = '';
        current.trim = '';
        current.vehicleKey = '';
      } else if (field === this.fields.model) {
        current.model = field.value;
        current.year = '';
        current.trim = '';
        current.vehicleKey = '';
      } else if (field === this.fields.year) {
        current.year = Number(field.value) || '';
        current.trim = '';
        current.vehicleKey = '';
      } else if (field === this.fields.trim) {
        current.trim = field.value;
      } else if (field === this.fields.category) {
        current.category = departmentValue(field.value);
      }

      this.populate(current);
      this.save();
    }

    baseProducts(selection) {
      if (!this.products?.length || !this.isComplete(selection)) return [];
      return this.products.filter((product) => productPotentiallyMatches(product, selection));
    }

    updateProducts(preferredCategory) {
      const selection = this.values();
      const products = this.baseProducts(selection);
      const counts = { all: products.length, exterior: 0, lighting: 0, interior: 0 };
      products.forEach((product) => {
        const department = departmentValue(product.fitment?.department);
        if (department !== 'all') counts[department] += 1;
      });

      const categoryOptions = ['all', 'exterior', 'lighting', 'interior']
        .filter((category) => category === 'all' || counts[category] > 0)
        .map((category) => ({
          value: category,
          label: `${departmentLabel(category)} (${counts[category]})`,
        }));

      if (this.fields.category) {
        const category = departmentValue(preferredCategory || this.fields.category.value);
        setOptions(this.fields.category, categoryOptions, {
          value: categoryOptions.some((option) => option.value === category) ? category : 'all',
          placeholder: 'Shop for',
        });
      }

      const category = departmentValue(this.fields.category?.value);
      const visibleProducts = products
        .filter((product) => category === 'all' || departmentValue(product.fitment?.department) === category)
        .sort((left, right) => Number(right.available) - Number(left.available));

      this.renderCategoryCounts(counts, selection);
      this.renderProducts(visibleProducts, selection);
      this.updateDestination(selection, category);
    }

    renderCategoryCounts(counts, selection) {
      if (this.resultCount) this.resultCount.textContent = String(counts.all);
      if (this.isComplete(selection)) {
        this.dataset.fitmentState = 'success';
        this.message(
          isUnsureTrim(selection.trim)
            ? `${counts.all} ${counts.all === 1 ? 'upgrade' : 'upgrades'} match your year and model. Confirm exact trim on the product page.`
            : `${counts.all} ${counts.all === 1 ? 'upgrade' : 'upgrades'} found for your vehicle.`
        );
      } else {
        this.dataset.fitmentState = 'idle';
      }

      if (!this.categoryCounts) return;
      this.categoryCounts.replaceChildren();

      ['all', 'exterior', 'lighting', 'interior'].forEach((category) => {
        if (category !== 'all' && !counts[category]) return;
        const item = element('span', 'kraven-fitment-count');
        const number = element('strong', 'kraven-fitment-count__number', String(counts[category]));
        const label = element('span', 'kraven-fitment-count__label', departmentLabel(category));
        item.append(number, label);
        this.categoryCounts.append(item);
      });
    }

    renderProducts(products, selection) {
      if (!this.results) return;
      this.results.replaceChildren();
      if (!this.isComplete(selection)) {
        if (this.resultsRoot) this.resultsRoot.hidden = true;
        return;
      }

      if (this.resultsRoot) this.resultsRoot.hidden = false;
      const limit = Number(this.dataset.productLimit) || PRODUCT_LIMIT;
      products.slice(0, limit).forEach((product) => {
        this.results.append(productCard(product, this.currency));
      });

      if (!products.length) {
        const empty = element('p', 'kraven-fitment-results__empty', 'No exact upgrades are listed in this category yet.');
        this.results.append(empty);
      }
    }

    updateDestination(selection, category) {
      const profile = this.profiles?.find((candidate) => candidate.key === selection.vehicleKey);
      const destination = profile?.destinations?.[category] || profile?.destinations?.all || '/collections/all';
      this.destination = withGarageParameters(destination, selection, category);
      if (this.viewAll) this.viewAll.href = this.destination;
    }

    refreshDependencies() {
      const make = this.fields.make?.value;
      const model = this.fields.model?.value;
      const year = this.fields.year?.value;
      const trimVisible = this.fields.trim && !fieldShell(this.fields.trim)?.hidden;
      const trimComplete = !trimVisible || Boolean(this.fields.trim.value);

      if (this.fields.make) this.fields.make.disabled = !this.ready;
      if (this.fields.model) this.fields.model.disabled = !this.ready || !make;
      if (this.fields.year) this.fields.year.disabled = !this.ready || !make || !model;
      if (this.fields.trim) {
        this.fields.trim.disabled = !this.ready || !make || !model || !year || !this.profile || !trimVisible;
      }
      if (this.fields.category) {
        this.fields.category.disabled =
          !this.ready || !make || !model || !year || !this.profile || !trimComplete;
      }
      if (this.submitButton) {
        this.submitButton.disabled = !this.ready || !this.isComplete();
        this.submitButton.setAttribute('aria-disabled', String(this.submitButton.disabled));
      }
    }

    refreshButton() {
      this.refreshDependencies();
    }

    save() {
      const selection = this.values();
      const current = GarageStore.read(this.storageKey);
      const changedVehicle = current.vehicleKey && current.vehicleKey !== selection.vehicleKey;
      const garage = GarageStore.write({
        ...selection,
        productAnswers: changedVehicle ? {} : current.productAnswers || {},
      }, this.storageKey);
      document.dispatchEvent(new CustomEvent('kraven:garage-updated', { detail: garage }));
      return garage;
    }

    submit(event) {
      event.preventDefault();
      if (!this.ready || !this.isComplete()) {
        this.message(this.dataset.messageSelect || copy.incomplete);
        this.refreshButton();
        return;
      }

      this.save();
      window.location.assign(this.destination || '/collections/all');
    }
  }

  function createSelectShell(name, labelText, hook) {
    // A non-label shell prevents mobile browsers from forwarding a tap on the
    // custom trigger to the visually hidden native select.
    const label = element('div', 'kraven-fitment-verify__field');
    label.dataset.fitmentFieldShell = '';
    const text = element('span', '', labelText);
    const select = document.createElement('select');
    select.name = name;
    select.required = true;
    select.setAttribute('aria-label', labelText);
    select.dataset.verifyField = '';
    if (hook) select.dataset[hook] = '';
    label.append(text, select);
    return { label, select };
  }

  function questionIsRelevant(question, profile, year) {
    const years = numberArray(question.years);
    if (years.length && !years.includes(Number(year))) return false;
    const vehicleKeys = asArray(question.vehicleKeys);
    if (vehicleKeys.length && !vehicleKeys.includes(profile?.key)) return false;
    const makes = asArray(question.makes);
    if (makes.length && !makes.some((make) => equal(make, profile?.make))) return false;
    const models = asArray(question.models);
    if (models.length && !models.some((model) => equal(model, profile?.model))) return false;
    return true;
  }

  function knownVehicleProductMatch(product, selection) {
    return productPotentiallyMatches(product, selection, false);
  }

  function variantMatchesVehicle(variant, optionNames, variants, selection, fitment) {
    const key = (value) => normalize(value).replace(/[^a-z0-9]/g, '');
    const trimValues = (value) => String(value).toUpperCase().match(/\b(?:XSE|XLE|TRD|LE|SE|L|NIGHTSHADE)\b/g) || [];
    const onlyTrims = (value) => /^(?:(?:XSE|XLE|TRD|LE|SE|L|NIGHTSHADE|HYBRID)|[\s/,&+()-])+$/i.test(String(value));

    return asArray(optionNames).every((name, index) => {
      const value = variant.options[index];
      const optionKey = key(name);
      const trims = trimValues(value);
      const trimOption = trims.length && (
        /trim|fitment/.test(optionKey) || variants.every((candidate) => onlyTrims(candidate.options[index]))
      );
      if (trimOption) {
        if (!selection.trim || isUnsureTrim(selection.trim)) return false;
        if (!applicationMatchesTrim({ trims }, selection.trim)) return false;
      }
      if (/^(?:modelyear|vehicleyear|year|years)$/.test(optionKey)) {
        const years = [...String(value).matchAll(/\b(19\d{2}|20\d{2})(?:\s*[-–—]\s*(19\d{2}|20\d{2}))?/g)]
          .flatMap((match) => {
            const first = Number(match[1]);
            const last = Number(match[2] || match[1]);
            return last >= first && last - first < 50
              ? Array.from({ length: last - first + 1 }, (_, offset) => first + offset)
              : [];
          });
        if (!years.length || !years.includes(Number(selection.year))) return false;
      }
      if (/^(?:make|vehiclemake)$/.test(optionKey) && !equal(value, selection.make)) return false;
      if (/^(?:model|vehiclemodel)$/.test(optionKey) && !equal(value, selection.model)) return false;

      const question = fitment.questions.find((candidate) =>
        [candidate.key, candidate.label, questionDisplayLabel(candidate)].some((label) => key(label) === optionKey)
      );
      if (question) {
        const answer = selection.answers[question.key];
        if (!answer || isUnsureAnswer(answer)) return false;
        if (!equal(value, answer) && !equal(value, questionOptionLabel(question, answer))) return false;
      }
      return true;
    });
  }

  class KravenFitmentVerify extends window.HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      removeVehicleType(this);
      this.productId = String(this.dataset.productId || '');
      this.productHandle = this.dataset.productHandle || productHandleFromLocation();
      this.storageKey = this.dataset.storageKey || GARAGE_KEY;
      this.fitment = normalizeFitment(embeddedJson(PRODUCT_FITMENT_SELECTOR, this));
      this.variantData = embeddedJson('[data-kraven-fitment-variants]', this) || {};
      this.catalog = normalizeCatalog(embeddedJson(CATALOG_SELECTOR, this));
      this.fieldsContainer = this.querySelector('[data-fitment-fields]');
      this.button = this.querySelector('[data-verify-button]');
      this.status = this.querySelector('[data-verify-status]');
      this.summary = this.querySelector('[data-fitment-summary]');
      this.vehicleSummary = this.querySelector('[data-fitment-vehicle]');
      this.configurationSummary = this.querySelector('[data-fitment-configuration]');
      this.editButton = this.querySelector('[data-fitment-edit]');
      this.verified = false;
      this.lastStateSignature = '';
      this.alternativeRequest = 0;

      this.button?.addEventListener('click', () => this.verify(), { signal });
      this.editButton?.addEventListener('click', () => this.edit(), { signal });
      this.addEventListener('click', (event) => this.handleMobileSelectButton(event), { signal });
      this.addEventListener('change', (event) => this.change(event), { signal });
      document.addEventListener('submit', (event) => this.guard(event), { capture: true, signal });
      document.addEventListener('change', (event) => {
        const picker = event.target.closest?.('variant-picker');
        if (picker && String(picker.dataset.productId) === this.productId && !picker.closest('product-card, quick-add-dialog')) {
          if (this.fields) this.invalidate();
          else this.setState('incomplete', false, { message: this.dataset.messageSelect || copy.incomplete });
        }
      }, { capture: true, signal });
      document.addEventListener('click', (event) => {
        const target = event.target.closest?.('.shopify-payment-button, shopify-accelerated-checkout');
        const form = target && this.matchingForms().find((component) => component.contains(target));
        if (form && !this.validatePurchase(form.querySelector('input[name="id"]')?.value || '')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal });
      document.addEventListener('kraven:fitment-before-add', (event) => {
        if (String(event.detail?.productId) !== this.productId) return;
        event.detail.checked = true;
        if (!this.validatePurchase(event.detail?.variantId)) event.preventDefault();
      }, { capture: true, signal });

      this.ensureAlternativeUI();
      this.setState('incomplete', false, { message: this.dataset.messageSelect || copy.incomplete });
      this.initialize();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.closeMobileSelectSheet(false, true);
    }

    enhanceMobileSelects() {
      this.querySelectorAll('select[data-verify-field]').forEach((select) => {
        if (select.dataset.kravenMobileNative !== undefined) return;
        select.dataset.kravenMobileNative = '';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'kraven-fitment-verify__mobile-select';
        button.dataset.mobileSelectTrigger = '';
        button.setAttribute('aria-haspopup', 'listbox');
        button.setAttribute('aria-expanded', 'false');
        button.addEventListener('pointerdown', (event) => event.preventDefault());

        const value = element('span', 'kraven-fitment-verify__mobile-select-value');
        value.dataset.mobileSelectValue = '';
        const caret = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        caret.setAttribute('viewBox', '0 0 12 8');
        caret.setAttribute('aria-hidden', 'true');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'm1.5 1.5 4.5 4.5 4.5-4.5');
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-width', '1.5');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        caret.append(path);
        button.append(value, caret);
        select.insertAdjacentElement('afterend', button);
      });
      this.syncMobileSelects();
    }

    syncMobileSelects() {
      this.querySelectorAll('select[data-kraven-mobile-native]').forEach((select) => {
        const button = select.nextElementSibling;
        if (!button?.matches?.('[data-mobile-select-trigger]')) return;
        const selected = select.selectedOptions[0];
        const value = button.querySelector('[data-mobile-select-value]');
        if (value) value.textContent = selected?.textContent?.trim() || copy.select;
        button.disabled = select.disabled;
        button.setAttribute('aria-disabled', String(select.disabled));
        const fieldLabel = fieldShell(select)?.querySelector(':scope > span')?.textContent?.trim() || select.name;
        button.setAttribute('aria-label', `${fieldLabel}: ${value?.textContent || copy.select}`);
      });
    }

    handleMobileSelectButton(event) {
      const button = event.target?.closest?.('[data-mobile-select-trigger]');
      if (!button || !this.contains(button) || !window.matchMedia('(max-width: 749px)').matches) return;
      event.preventDefault();
      event.stopPropagation();
      const select = button.previousElementSibling;
      if (!(select instanceof HTMLSelectElement) || select.disabled) return;
      this.openMobileSelectSheet(select, button);
    }

    openMobileSelectSheet(select, trigger) {
      this.closeMobileSelectSheet(false, true);

      const overlay = element('div', 'kraven-mobile-select-sheet');
      overlay.dataset.mobileSelectSheet = '';
      const panel = element('div', 'kraven-mobile-select-sheet__panel');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      const head = element('div', 'kraven-mobile-select-sheet__head');
      const title = element(
        'strong',
        'kraven-mobile-select-sheet__title',
        fieldShell(select)?.querySelector(':scope > span')?.textContent?.trim() || 'Choose an option'
      );
      const close = element('button', 'kraven-mobile-select-sheet__close', 'Close');
      close.type = 'button';
      close.setAttribute('aria-label', 'Close options');
      head.append(title, close);

      const list = element('div', 'kraven-mobile-select-sheet__list');
      list.setAttribute('role', 'listbox');
      [...select.options].filter((option) => option.value).forEach((option) => {
        const choice = element('button', 'kraven-mobile-select-sheet__option', option.textContent?.trim() || option.value);
        choice.type = 'button';
        choice.dataset.value = option.value;
        choice.setAttribute('role', 'option');
        choice.setAttribute('aria-selected', String(option.selected));
        choice.disabled = option.disabled;
        if (option.selected) choice.classList.add('is-selected');
        choice.addEventListener('click', (event) => {
          // Keep the tapped option in the DOM until the click has completed.
          // Removing a focused/tapped control synchronously makes mobile
          // browsers re-anchor the document to an unrelated element.
          event.preventDefault();
          event.stopPropagation();
          choice.blur();
          select.value = option.value;
          this.syncMobileSelects();
          select.dispatchEvent(new Event('change', { bubbles: true }));
          this.closeMobileSelectSheet(false, false);
        });
        list.append(choice);
      });

      close.addEventListener('click', () => this.closeMobileSelectSheet());
      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) this.closeMobileSelectSheet();
      });
      overlay.addEventListener('touchmove', (event) => {
        if (event.target === overlay) event.preventDefault();
      }, { passive: false });
      overlay.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeMobileSelectSheet();
      });

      panel.append(head, list);
      overlay.append(panel);
      document.body.append(overlay);
      this.mobileSelectSheet = {
        overlay,
        trigger,
        viewport: { left: window.scrollX, top: window.scrollY },
      };
      trigger.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => overlay.classList.add('is-open'));
    }

    closeMobileSelectSheet(restoreFocus = false, immediate = false, viewport = null) {
      const sheet = this.mobileSelectSheet;
      if (!sheet) return;
      this.mobileSelectSheet = null;
      if (!viewport) viewport = sheet.viewport;
      sheet.trigger?.setAttribute('aria-expanded', 'false');
      sheet.overlay.classList.remove('is-open');
      const holdViewport = () => {
        if (!viewport || !window.matchMedia('(max-width: 749px)').matches) return;
        if (Math.abs(window.scrollY - viewport.top) > 0.5 || Math.abs(window.scrollX - viewport.left) > 0.5) {
          window.scrollTo({ left: viewport.left, top: viewport.top, behavior: 'instant' });
        }
      };
      holdViewport();
      if (viewport && window.matchMedia('(max-width: 749px)').matches) {
        let cancelled = false;
        const releaseAt = window.performance.now() + 700;
        const cancelHold = () => {
          cancelled = true;
          window.removeEventListener('pointerdown', cancelHold, true);
          window.removeEventListener('touchstart', cancelHold, true);
          window.removeEventListener('wheel', cancelHold, true);
        };
        const keepHolding = () => {
          if (cancelled || window.performance.now() >= releaseAt) {
            cancelHold();
            return;
          }
          holdViewport();
          requestAnimationFrame(keepHolding);
        };
        window.addEventListener('pointerdown', cancelHold, { capture: true, once: true });
        window.addEventListener('touchstart', cancelHold, { capture: true, once: true, passive: true });
        window.addEventListener('wheel', cancelHold, { capture: true, once: true, passive: true });
        requestAnimationFrame(keepHolding);
      }
      if (immediate) {
        sheet.overlay.remove();
        holdViewport();
      } else {
        window.setTimeout(() => {
          sheet.overlay.remove();
          holdViewport();
        }, 190);
      }
      if (restoreFocus) sheet.trigger?.focus?.({ preventScroll: true });
    }

    async initialize() {
      if (!this.fieldsContainer) {
        this.setState('review', false, { message: this.dataset.messageReview || copy.review });
        return;
      }

      if (!this.catalog.vehicles.length) {
        try {
          const feed = await loadFitmentFeed(this);
          this.catalog = feed.catalog;
        } catch (error) {
          console.warn('[Kraven fitment] Vehicle catalog unavailable.', error);
        }
      }

      if (!this.catalog.vehicles.length || !this.fitment?.applications.length) {
        this.setState('review', false, {
          message: this.dataset.messageReview || copy.review,
        });
        return;
      }

      this.profiles = this.catalog.vehicles;
      this.renderBaseFields();
      this.restore();
    }

    renderBaseFields() {
      this.fieldsContainer.replaceChildren();
      const make = createSelectShell('make', this.dataset.labelMake || 'Make', 'fitmentMake');
      const model = createSelectShell('model', this.dataset.labelModel || 'Model', 'fitmentModel');
      const year = createSelectShell('year', this.dataset.labelYear || 'Year', 'fitmentYear');
      const trim = createSelectShell('trim', this.dataset.labelTrim || 'Trim', 'fitmentTrim');

      this.fields = {
        make: make.select,
        model: model.select,
        year: year.select,
        trim: trim.select,
      };
      this.fieldsContainer.append(make.label, model.label, year.label, trim.label);
      this.questionsContainer = this.querySelector('[data-verify-questions]') || this.fieldsContainer;
      this.enhanceMobileSelects();
    }

    restore() {
      const garage = GarageStore.read(this.storageKey);
      const answers = garage.productAnswers?.[this.productHandle] || {};
      this.populateBase({
        make: garage.make,
        model: garage.model,
        year: garage.year,
        trim: garage.trim,
        vehicleKey: garage.vehicleKey,
        answers,
      });

      if (this.isComplete()) this.verify({ restoring: true });
    }

    makeOptions() {
      return unique(this.profiles.map((profile) => profile.make))
        .sort((left, right) => left.localeCompare(right))
        .map((value) => ({ value, label: value }));
    }

    modelOptions(make) {
      return unique(
        this.profiles.filter((profile) => equal(profile.make, make)).map((profile) => profile.model)
      )
        .sort((left, right) => left.localeCompare(right))
        .map((value) => ({ value, label: value }));
    }

    yearOptions(make, model) {
      return unique(
        this.profiles
          .filter((profile) => equal(profile.make, make) && equal(profile.model, model))
          .flatMap(profileYears)
      )
        .sort((left, right) => Number(right) - Number(left))
        .map((value) => ({ value: String(value), label: String(value) }));
    }

    populateBase(preferred = {}) {
      const make = setOptions(this.fields.make, this.makeOptions(), { value: preferred.make });
      const model = setOptions(this.fields.model, this.modelOptions(make), {
        value: preferred.model,
        autoSelectSingle: true,
      });
      const year = setOptions(this.fields.year, this.yearOptions(make, model), { value: preferred.year });
      const preferredKeys = this.fitment.applications.map((application) => application.vehicleKey);
      const profile = resolveProfile(
        this.profiles,
        { make, model, year, vehicleKey: preferred.vehicleKey },
        preferredKeys
      );
      const trims = profileTrims(profile, year);

      showField(this.fields.trim, trims.length > 0);
      setOptions(
        this.fields.trim,
        trimOptions(trims),
        { value: preferred.trim }
      );

      this.profile = profile;
      this.renderQuestions(preferred.answers || {});
      this.refreshDependencies();
    }

    renderQuestions(preferredAnswers = {}) {
      this.questionsContainer
        .querySelectorAll('[data-fitment-question-shell]')
        .forEach((question) => question.remove());

      this.questionFields = new Map();
      const year = Number(this.fields.year.value) || '';
      this.fitment.questions
        .filter((question) => questionIsRelevant(question, this.profile, year))
        .forEach((question) => {
          const displayLabel = questionDisplayLabel(question);
          const shell = createSelectShell(question.key, displayLabel, 'fitmentQuestion');
          shell.label.dataset.fitmentQuestionShell = '';
          shell.select.dataset.questionKey = question.key;
          setOptions(
            shell.select,
            asArray(question.options).map((value) => ({ value, label: questionOptionLabel(question, value) })),
            { value: preferredAnswers[question.key] }
          );
          this.questionFields.set(question.key, {
            field: shell.select,
            question: { ...question, displayLabel },
          });
          this.questionsContainer.append(shell.label);
        });
      this.enhanceMobileSelects();
      this.refreshDependencies();
    }

    values() {
      const trimVisible = !fieldShell(this.fields.trim)?.hidden;
      const answers = {};
      this.questionFields?.forEach(({ field }, key) => {
        answers[key] = field.value;
      });

      const base = {
        make: this.fields.make.value,
        model: this.fields.model.value,
        year: Number(this.fields.year.value) || '',
        trim: trimVisible ? this.fields.trim.value : '',
        answers,
      };
      const preferredKeys = this.fitment?.applications.map((application) => application.vehicleKey) || [];
      const profile = resolveProfile(
        this.profiles || [],
        { ...base, vehicleKey: this.profile?.key },
        preferredKeys
      );
      return { ...base, vehicleKey: profile?.key || '' };
    }

    isComplete(selection = this.values()) {
      if (!selection.make || !selection.model || !selection.year || !selection.vehicleKey) return false;
      const profile = this.profiles.find((candidate) => candidate.key === selection.vehicleKey);
      if (profileTrims(profile, selection.year).length && !selection.trim) return false;
      return [...(this.questionFields?.values() || [])].every(({ field }) => Boolean(field.value));
    }

    change(event) {
      const field = event.target.closest('select');
      if (!field || !this.contains(field)) return;

      // The mobile option sheet writes back to the same native fields, keeping
      // the compatibility engine and saved-garage data as the source of truth.
      const current = this.values();
      if (field === this.fields.make) {
        current.make = field.value;
        current.model = '';
        current.year = '';
        current.trim = '';
        current.vehicleKey = '';
        current.answers = {};
      } else if (field === this.fields.model) {
        current.model = field.value;
        current.year = '';
        current.trim = '';
        current.vehicleKey = '';
        current.answers = {};
      } else if (field === this.fields.year) {
        current.year = Number(field.value) || '';
        current.trim = '';
        current.vehicleKey = '';
        current.answers = {};
      } else if (field === this.fields.trim) {
        current.trim = field.value;
      } else if (field.dataset.questionKey) {
        current.answers[field.dataset.questionKey] = field.value;
      }

      this.populateBase(current);
      this.saveGarage();
      this.invalidate();

      this.syncMobileSelects();
    }

    unsureAnswers(selection) {
      return [...(this.questionFields?.values() || [])]
        .filter(({ question }) => isUnsureAnswer(selection.answers[question.key]))
        .map(({ question }) => ({ key: question.key, label: question.displayLabel || question.label }));
    }

    selectedVariant() {
      const variants = asArray(this.variantData.variants);
      const picker = [...document.querySelectorAll('variant-picker')].find((candidate) =>
        String(candidate.dataset.productId) === this.productId && !candidate.closest('product-card, quick-add-dialog')
      );
      if (picker) {
        const selected = [...picker.querySelectorAll('fieldset')].map((field) => field.querySelector('input:checked')?.value);
        const dropdowns = [...picker.querySelectorAll('select')].map((select) => select.value);
        const options = selected.length ? selected : dropdowns;
        if (options.length) {
          return variants.find((variant) => variant.options.length === options.length &&
            variant.options.every((value, index) => equal(value, options[index]))) || null;
        }
      }
      const id = this.matchingForms()[0]?.querySelector('input[name="id"]')?.value;
      return variants.find((variant) => String(variant.id) === String(id)) || null;
    }

    verificationFingerprint(selection, variant) {
      return JSON.stringify([String(variant?.id || ''), selection.vehicleKey, selection.year, selection.trim,
        Object.entries(selection.answers || {}).sort(([left], [right]) => left.localeCompare(right))]);
    }

    evaluate(selection) {
      if (!this.fitment?.applications.length) {
        return { state: 'review', verified: false, message: this.dataset.messageReview || copy.review };
      }
      if (!this.isComplete(selection)) {
        return { state: 'incomplete', verified: false, message: this.dataset.messageSelect || copy.incomplete };
      }

      const variant = this.selectedVariant();
      if (!variant) {
        return { state: 'incomplete', verified: false, message: this.dataset.messageVariantPending || copy.incomplete };
      }
      if (!variantMatchesVehicle(variant, this.variantData.optionNames, this.variantData.variants, selection, this.fitment)) {
        return { state: 'incompatible', verified: false, variantMismatch: true, message: this.dataset.messageVariantMismatch || copy.incompatible };
      }

      const year = Number(selection.year);
      const vehicleApplications = this.fitment.applications.filter(
        (application) => application.vehicleKey === selection.vehicleKey && application.years.includes(year)
      );
      const trimApplications = vehicleApplications.filter((application) =>
        applicationMatchesTrim(application, selection.trim)
      );

      if (!trimApplications.length) {
        return {
          state: 'incompatible',
          verified: false,
          message: this.dataset.messageIncompatible || this.dataset.messageUnsupported || copy.incompatible,
        };
      }

      const unsureAnswers = this.unsureAnswers(selection);
      if (unsureAnswers.length) {
        const unsureKeys = new Set(unsureAnswers.map((answer) => answer.key));
        const provisionalApplication = trimApplications.find((candidate) =>
          Object.entries(candidate.conditions || {}).every(([key, allowed]) => {
            if (unsureKeys.has(key)) return true;
            const selected = selection.answers[key];
            return asArray(allowed).some((value) => equal(value, selected));
          })
        );
        if (!provisionalApplication) {
          return {
            state: 'incompatible',
            verified: false,
            message: this.dataset.messageIncompatible || this.dataset.messageUnsupported || copy.incompatible,
          };
        }
        return {
          state: 'review',
          verified: false,
          rule: provisionalApplication,
          configurationUnconfirmed: true,
          unsureAnswers: unsureAnswers.map((answer) => answer.label),
          message: this.dataset.messageReview || copy.review,
        };
      }

      const missingCondition = trimApplications.some((application) =>
        Object.keys(application.conditions || {}).some((key) => !selection.answers[key])
      );
      const application = trimApplications.find((candidate) =>
        applicationMatchesConditions(candidate, selection.answers, false)
      );

      if (!application) {
        if (missingCondition) {
          return { state: 'review', verified: false, message: this.dataset.messageReview || copy.review };
        }
        return {
          state: 'incompatible',
          verified: false,
          message: this.dataset.messageIncompatible || this.dataset.messageUnsupported || copy.incompatible,
        };
      }

      const requiresReview =
        normalize(application.verification) === 'review' || normalize(this.fitment.policy) === 'visual_review';

      if (isUnsureTrim(selection.trim)) {
        return {
          state: 'review',
          verified: false,
          rule: application,
          trimUnconfirmed: true,
          configurationUnconfirmed: true,
          message: this.dataset.messageReview || copy.review,
        };
      }

      if (requiresReview) {
        return {
          state: 'review',
          verified: false,
          rule: application,
          fitmentReviewRequested: true,
          message: this.dataset.messageReview || copy.review,
        };
      }

      if (normalize(application.verification) !== 'confirmed') {
        return {
          state: 'review',
          verified: false,
          rule: application,
          message: this.dataset.messageReview || copy.review,
        };
      }

      return {
        state: 'match',
        verified: true,
        rule: application,
        message: isUnsureTrim(selection.trim)
          ? this.dataset.messageUnsureMatch || copy.unsureMatch
          : this.dataset.messageMatch || copy.match,
      };
    }

    verify() {
      const selection = this.values();
      const result = this.evaluate(selection);
      this.setState(result.state, result.verified, { ...result, selection });

      if (result.verified) {
        this.verifiedFingerprint = this.verificationFingerprint(selection, this.selectedVariant());
        this.saveGarage();
        this.injectProperties(selection, result);
        this.showSummary(selection);
      } else {
        this.removeProperties();
        if (!result.variantMismatch && (result.state === 'incompatible' || result.state === 'review')) {
          this.loadAlternatives(selection, result.state);
        }
      }
    }

    invalidate() {
      this.verified = false;
      this.removeProperties();
      this.summary && (this.summary.hidden = true);
      this.fieldsContainer.hidden = false;
      if (this.button) this.button.hidden = false;
      if (this.alternativesRoot) this.alternativesRoot.hidden = true;
      this.setState('incomplete', false, { message: '' });
      this.refreshDependencies();
    }

    edit() {
      this.invalidate();
      if (this.status) this.status.textContent = '';
      this.fieldsContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    setState(state, verified, options = {}) {
      this.verified = Boolean(verified);
      this.dataset.verificationState = state;
      if (this.status) this.status.textContent = options.message || '';
      if (!verified) this.removeProperties();
      if (!verified) this.verifiedFingerprint = '';

      const signature = `${state}:${Boolean(verified)}`;
      if (signature !== this.lastStateSignature) {
        this.lastStateSignature = signature;
        document.dispatchEvent(
          new CustomEvent('kraven:fitment-state', {
            detail: {
              productId: String(this.productId),
              state,
              verified: Boolean(verified),
            },
          })
        );
      }
    }

    showSummary(selection) {
      if (!this.summary) return;
      if (this.vehicleSummary) {
        this.vehicleSummary.textContent = `${selection.year} ${selection.make} ${selection.model}`;
      }
      if (this.configurationSummary) {
        const trimLabel = isUnsureTrim(selection.trim) ? UNSURE_TRIM_LABEL : selection.trim;
        const details = [trimLabel, ...Object.values(selection.answers)].filter(Boolean);
        this.configurationSummary.textContent = details.join(' / ');
      }
      this.fieldsContainer.hidden = true;
      if (this.button) this.button.hidden = true;
      this.summary.hidden = false;
    }

    saveGarage() {
      const selection = this.values();
      const current = GarageStore.read(this.storageKey);
      const changedVehicle = current.vehicleKey && current.vehicleKey !== selection.vehicleKey;
      const productAnswers = changedVehicle ? {} : { ...(current.productAnswers || {}) };
      productAnswers[this.productHandle] = selection.answers;
      const garage = GarageStore.write(
        {
          vehicleKey: selection.vehicleKey,
          make: selection.make,
          model: selection.model,
          year: selection.year,
          trim: selection.trim,
          category: current.category || 'all',
          productAnswers,
        },
        this.storageKey
      );
      document.dispatchEvent(new CustomEvent('kraven:garage-updated', { detail: garage }));
      return garage;
    }

    matchingForms() {
      return [...document.querySelectorAll('product-form-component')].filter(
        (component) => String(component.dataset.productId || '') === this.productId
      );
    }

    removeProperties() {
      this.matchingForms().forEach((component) => {
        component.querySelectorAll('[data-kraven-fitment-property]').forEach((input) => input.remove());
      });
    }

    injectProperties(selection, result) {
      this.removeProperties();
      const configurationUnconfirmed = Boolean(result.configurationUnconfirmed || isUnsureTrim(selection.trim));
      const properties = {
        Vehicle: `${selection.year} ${selection.make} ${selection.model}`,
        ...(selection.trim
          ? { Trim: isUnsureTrim(selection.trim) ? 'Unsure — confirmation requested' : selection.trim }
          : {}),
        ...Object.fromEntries(
          [...this.questionFields.values()].map(({ question }) => [
            question.displayLabel || question.label,
            questionOptionLabel(question, selection.answers[question.key]),
          ])
        ),
        ...(configurationUnconfirmed
          ? { '_Fitment confirmation requested': (result.unsureAnswers || ['Trim']).join(', ') }
          : {}),
        '_Fitment status': configurationUnconfirmed
          ? 'Vehicle range matched — configuration confirmation requested'
          : result.fitmentReviewRequested
            ? 'Vehicle range matched — fitment review requested'
          : result.state === 'match'
            ? 'Confirmed match'
            : 'Visual confirmation recorded',
        '_Fitment rule': result.rule?.id || 'manual-review',
        '_Fitment variant': String(this.selectedVariant()?.id || ''),
        '_Vehicle key': selection.vehicleKey,
        '_Fitment version': `${this.catalog.version}.${this.fitment.version}`,
      };

      this.matchingForms().forEach((component) => {
        const form = component.querySelector('form');
        if (!form) return;
        Object.entries(properties).forEach(([key, value]) => {
          if (!value) return;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = String(value);
          input.dataset.kravenFitmentProperty = '';
          form.append(input);
        });
      });
    }

    validatePurchase(variantId) {
      // The fitment check is optional; only attach details after an exact, current verification.
      if (!this.fields) return true;
      const current = this.values();
      const variant = this.selectedVariant();
      const result = this.evaluate(current);
      const exactVariant = variantId == null || String(variant?.id) === String(variantId);
      const exactVerification = this.verified && this.verifiedFingerprint === this.verificationFingerprint(current, variant);
      if (result.verified && exactVariant && exactVerification) {
        this.injectProperties(current, result);
      } else if (this.verified) {
        this.invalidate();
      } else {
        this.removeProperties();
      }
      return true;
    }

    guard(event) {
      const component = event.target.closest?.('product-form-component');
      if (!component || String(component.dataset.productId || '') !== this.productId) return;
      const variantId = component.querySelector('input[name="id"]')?.value;
      if (this.validatePurchase(variantId)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    ensureAlternativeUI() {
      let root = this.querySelector('[data-fitment-recovery]');
      if (!root) {
        root = element('section', 'kraven-fitment-alternatives');
        root.dataset.fitmentRecovery = '';
        const heading = element('h3', 'kraven-fitment-alternatives__heading');
        heading.dataset.fitmentAlternativesHeading = '';
        const body = element('p', 'kraven-fitment-alternatives__body');
        body.dataset.fitmentAlternativesBody = '';
        const products = element('div', 'kraven-fitment-alternatives__products');
        products.dataset.fitmentAlternativeProducts = '';
        const cta = element('a', 'kraven-fitment-alternatives__cta');
        cta.dataset.fitmentAlternativesCta = '';
        root.append(heading, body, products, cta);
        this.append(root);
      }

      this.alternativesRoot = root;
      this.alternativesHeading = root.querySelector('[data-fitment-alternatives-heading], h3');
      this.alternativesBody = root.querySelector('[data-fitment-alternatives-body]');
      this.alternativesProducts = root.querySelector(
        '[data-fitment-alternatives], [data-fitment-alternative-products]'
      );
      this.alternativesCta = root.querySelector('[data-fitment-view-all], [data-fitment-alternatives-cta]');
      root.hidden = true;
    }

    async loadAlternatives(selection, state) {
      if (!selection.vehicleKey || !selection.year || !this.alternativesRoot) return;
      const request = ++this.alternativeRequest;
      this.alternativesRoot.hidden = false;
      this.alternativesRoot.setAttribute('aria-busy', 'true');
      this.alternativesProducts?.setAttribute('aria-busy', 'true');
      if (this.alternativesHeading) {
        this.alternativesHeading.textContent =
          state === 'incompatible' ? 'Parts that fit your vehicle' : 'Need another fitment route?';
      }
      if (this.alternativesBody) {
        this.alternativesBody.textContent =
          state === 'incompatible'
            ? 'These upgrades match the vehicle details you selected.'
            : 'Explore confirmed possibilities or contact us before ordering if a factory detail is unclear.';
      }

      const profile = this.profiles.find((candidate) => candidate.key === selection.vehicleKey);
      const category = departmentValue(this.fitment?.department);
      const destination =
        profile?.destinations?.[category] ||
        profile?.destinations?.all ||
        this.fitment?.vehicleCollection ||
        '/collections/all';
      if (this.alternativesCta) {
        this.alternativesCta.href = withGarageParameters(destination, selection, category);
        this.alternativesCta.textContent = `View all ${profile?.model || 'vehicle'} upgrades`;
      }

      try {
        const feed = await loadFitmentFeed(this);
        if (request !== this.alternativeRequest) return;
        const explicit = new Map(this.fitment.alternatives.map((handle, index) => [handle, index]));
        const products = feed.products
          .filter((product) => product.handle !== this.productHandle)
          .filter((product) => knownVehicleProductMatch(product, selection))
          .sort((left, right) => {
            if (left.available !== right.available) return Number(right.available) - Number(left.available);
            const leftExplicit = explicit.has(left.handle) ? explicit.get(left.handle) : 999;
            const rightExplicit = explicit.has(right.handle) ? explicit.get(right.handle) : 999;
            if (leftExplicit !== rightExplicit) return leftExplicit - rightExplicit;
            const leftDepartment = departmentValue(left.fitment?.department) === category ? 0 : 1;
            const rightDepartment = departmentValue(right.fitment?.department) === category ? 0 : 1;
            if (leftDepartment !== rightDepartment) return leftDepartment - rightDepartment;
            return left.title.localeCompare(right.title);
          });

        if (this.alternativesProducts) {
          this.alternativesProducts.replaceChildren();
          const limit = Number(this.dataset.alternativeLimit) || 4;
          products.slice(0, limit).forEach((product) => {
            this.alternativesProducts.append(productCard(product, feed.currency, 'For your vehicle'));
          });
          if (!products.length) {
            this.alternativesProducts.append(
              element('p', 'kraven-fitment-alternatives__empty', 'No exact alternative is listed yet. Our fitment team can help.')
            );
          }
        }
      } catch (error) {
        console.warn('[Kraven fitment] Alternatives could not be loaded.', error);
        if (this.alternativesProducts) {
          this.alternativesProducts.replaceChildren(
            element('p', 'kraven-fitment-alternatives__empty', 'Browse the vehicle collection or contact us for fitment help.')
          );
        }
      } finally {
        if (request === this.alternativeRequest) {
          this.alternativesRoot.setAttribute('aria-busy', 'false');
          this.alternativesProducts?.setAttribute('aria-busy', 'false');
        }
      }
    }

    refreshDependencies() {
      if (!this.fields) return;
      const make = this.fields.make.value;
      const model = this.fields.model.value;
      const year = this.fields.year.value;
      const trimVisible = !fieldShell(this.fields.trim)?.hidden;
      const trimComplete = !trimVisible || Boolean(this.fields.trim.value);

      this.fields.make.disabled = false;
      this.fields.model.disabled = !make;
      this.fields.year.disabled = !make || !model;
      this.fields.trim.disabled = !make || !model || !year || !this.profile || !trimVisible;
      this.questionFields?.forEach(({ field }) => {
        field.disabled = !make || !model || !year || !this.profile || !trimComplete;
      });
      if (this.button) {
        this.button.disabled = !this.isComplete();
        this.button.setAttribute('aria-disabled', String(this.button.disabled));
      }
      this.syncMobileSelects();
    }
  }

  if (!window.customElements.get('kraven-fitment-finder')) {
    window.customElements.define('kraven-fitment-finder', KravenFitmentFinder);
  }

  if (!window.customElements.get('kraven-fitment-verify')) {
    window.customElements.define('kraven-fitment-verify', KravenFitmentVerify);
  }
})();

